import React, { useEffect } from 'react'
import { useNovu } from '@novu/react'
import { App, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { findNovuInvalidation } from '../../shared/queries/cacheInvalidationGraph'
import { NotificationResult, parseObjectType } from './useNovuQueryInvalidation'

type RefreshButtonProps = {
  onRefresh: () => void
  label: string
}

const RefreshButton: React.FC<RefreshButtonProps> = ({ onRefresh, label }) => (
  <Button size="small" onClick={onRefresh}>
    {label}
  </Button>
)

const useNovuNotificationPopup = () => {
  const novu = useNovu()
  const { notification } = App.useApp()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  useEffect(() => {
    const handleReceived = ({ result }: { result: NotificationResult }) => {
      if (result.data?.popup !== 'true') return

      const invalidation = findNovuInvalidation(parseObjectType(result.data?.object))

      const hasActiveReportQueries = invalidation?.reports.some(queryKey =>
        queryClient
          .getQueryCache()
          .findAll({ queryKey })
          .some(q => q.getObserversCount() > 0)
      )

      const key = `novu-${Date.now()}`
      notification.info({
        key,
        message: result.subject,
        description: result.body,
        duration: 10,
        actions: hasActiveReportQueries ? (
          <RefreshButton
            label={t('notifications:novu:refreshButton')}
            onRefresh={() => {
              invalidation!.reports.forEach(queryKey => queryClient.refetchQueries({ queryKey }))
              notification.destroy(key)
            }}
          />
        ) : undefined
      })
    }

    const cleanup = novu.on('notifications.notification_received', handleReceived)
    return () => {
      cleanup()
    }
  }, [novu, notification, queryClient])
}

export default useNovuNotificationPopup
